import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/shared/components/ui/sheet";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import {
  Position,
  WorkOrder,
} from "@/services/backend/htz/work-order/types/work-order";
import { AhuComponentsSchema } from "@/services/backend/htz/ahu/components/ahu-components-schema";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import { AirHandlingUnitData } from "@/services/backend/htz/ahu/types/air-handling-unit-data";
import { AhuComponentsTable } from "@/services/backend/htz/ahu/components/ahu-components-table";
import {
  InspectionContextProvider,
  OnPositionChange,
  useInspectionContext,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/inspection-context";
import {
  AddStructuralAssessmentButton,
  StructuralInspection,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/structural-inspection";
import { useEffect, useState } from "react";
import { AddComponentDropdown } from "@/services/backend/htz/ahu/components/add-component-dropdown";
import { Switch } from "@/shared/components/ui/switch";
import { Label } from "@/shared/components/ui/label";
import { Camera, CheckCircle, Eye, RefreshCw, Tags } from "lucide-react";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/shared/components/ui/resizable";
import {
  AddVisualAssessmentButton,
  VisualInspection,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/visual-inspection";
import {
  AddPhotoButton,
  PhotoDocumentation,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/photo-documentation";
import {
  AddMicrobialAssessmentButton,
  MicrobialInspection,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/microbial-inspection";
import {
  useGetAirHandlingUnitData,
  usePutAirHandlingUnitData,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-air-handling-unit-data-offline";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useGetAhuComponentCatalog } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-ahu-component-catalog-offline";
import { useErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { hasFieldError } from "@/shared/lib/errors/validation-error";
import { cn } from "@/shared/lib/utils";
import { TextInput } from "@/shared/components/form/text-input";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";

export function InspectionSheet({
  workOrder,
  position,
  onPositionChange,
  disabled: propDisabled,
}: {
  workOrder: WorkOrder;
  position: Position;
  onPositionChange: OnPositionChange;
  disabled?: boolean;
}) {
  const disabled = !position.ahuDataId || workOrder.immutable || propDisabled;

  const { notReadyError } = useErrDocumentNotReady();
  const errorPrefix = `position.${position.id}.`;
  const hasError = hasFieldError(
    notReadyError,
    `${errorPrefix}structuralAssessment`,
    `${errorPrefix}visualAssessment`,
    `${errorPrefix}microbialAssessment`,
    `${errorPrefix}photo`,
  );

  return (
    <InspectionContextProvider
      position={position}
      onPositionChange={onPositionChange}
      disabled={disabled}
    >
      <Sheet>
        <SheetTrigger asChild disabled={disabled}>
          <Button
            size="sm"
            variant="ghost"
            className={cn("h-8", hasError && "border border-red-500 shadow-md")}
          >
            {t("Inspektion")}
          </Button>
        </SheetTrigger>
        <SheetContent
          side="bottom"
          className="h-screen space-y-4 px-4"
          // react-resizable-panels v4 handles resize gestures via document-level
          // pointer/focus listeners. Radix's DismissableLayer misreads those as an
          // outside interaction and closes the sheet, so ignore dismissals that
          // originate from a resize handle.
          onInteractOutside={(event) => {
            const target = event.target as HTMLElement | null;
            if (target?.closest("[data-slot='resizable-handle']")) {
              event.preventDefault();
            }
          }}
        >
          <InspectionSheetHeader />
          <InspectionSheetMain workOrder={workOrder} />
        </SheetContent>
      </Sheet>
    </InspectionContextProvider>
  );
}

function InspectionSheetHeader() {
  const { position } = useInspectionContext();
  const { data: airHandlingUnit } = useGetAirHandlingUnitData(
    position.ahuDataId ?? "",
  );

  return (
    <SheetHeader className="max-h-[10vh]">
      <SheetTitle className="flex justify-between pr-12">
        <span>
          {t("Inspektion für Position: ")}
          {position.number}
          {t(" - Anlage: ")}
          {airHandlingUnit?.designation}
        </span>
        <PersistenceStateUsingContext />
        <InspectionModeSwitch />
      </SheetTitle>
      <SheetDescription>
        {t(
          "Im Modus Aufnahme, können die Komponenten der Anlage zusammengebaut werden. Im Modus Inspektion können Komponenten ausgewählt werden um für diese Beurteilungen zu erstellen.",
        )}
      </SheetDescription>
    </SheetHeader>
  );
}

function InspectionSheetMain({ workOrder }: { workOrder: WorkOrder }) {
  const { position, active } = useInspectionContext();

  const { data: ahuData } = useGetAirHandlingUnitData(position.ahuDataId ?? "");

  if (!ahuData) {
    return null;
  }

  if (!active) {
    return <AhuCard ahuData={ahuData} workOrderId={position.workOrderId} />;
  }

  return (
    <ResizablePanelGroup className="max-h-[80vh] gap-4">
      <ResizablePanel>
        <AhuCard ahuData={ahuData} workOrderId={position.workOrderId} />
      </ResizablePanel>
      <ResizableHandle withHandle />
      <ResizablePanel>
        <AssessmentCard workOrder={workOrder} />
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}

function AhuCard({
  ahuData,
  workOrderId,
}: {
  ahuData: AirHandlingUnitData;
  workOrderId: string;
}) {
  const [request, setRequest] = useState(ahuData);
  const [mustSave, setMustSave] = useState(false);
  const [put, { isLoading, error, reset }] = usePutAirHandlingUnitData();

  const { toast } = useToast();
  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error),
        variant: "destructive",
      });
      reset();
    }
  }, [error, reset, toast]);

  const onChange = (ahu: AirHandlingUnitData) => {
    setMustSave(true);
    setRequest(ahu);
  };

  const doSave = () => {
    if (isLoading) {
      return;
    }

    put(request);
    setMustSave(false);
  };

  const { active } = useInspectionContext();
  const [tabValue, setTabValue] = useState<string>("schema");
  const { data: ahuComponentCatalog } = useGetAhuComponentCatalog(
    workOrderId,
    ahuData.companyId,
  );

  if (!ahuComponentCatalog) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Anlage")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <AhuBaseDataFields ahu={request} onAhuChange={onChange} error={error} />
        <Tabs value={tabValue} onValueChange={setTabValue}>
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="schema">{t("Schema")}</TabsTrigger>
              <TabsTrigger value="list">{t("Liste")}</TabsTrigger>
            </TabsList>
            {mustSave && (
              <div className="border-destructive/50 text-destructive inline-flex h-10 items-center justify-center rounded-lg border px-4 py-2 font-medium tracking-tight">
                {t("Änderungen nicht gespeichert")}
              </div>
            )}
            <div className="space-x-2">
              {tabValue === "list" && !active && (
                <AddComponentDropdown
                  ahuData={ahuData}
                  onAhuDataChange={onChange}
                  catalog={ahuComponentCatalog}
                />
              )}
              {!active && (
                <Button onClick={doSave} disabled={isLoading}>
                  {isLoading && (
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {t("Speichern")}
                </Button>
              )}
            </div>
          </div>
          <TabsContent value="schema">
            <AhuComponentsSchema
              ahuData={request}
              onAhuDataChange={onChange}
              ahuComponentCatalog={ahuComponentCatalog}
              className="h-[57vh]"
            />
          </TabsContent>
          <TabsContent value="list" className="h-[57vh]">
            <AhuComponentsTable ahu={request} onAhuChange={onChange} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function AhuBaseDataFields({
  ahu,
  onAhuChange,
  error,
}: {
  ahu: AirHandlingUnitData;
  onAhuChange: (ahu: AirHandlingUnitData) => void;
  error: unknown;
}) {
  return (
    <div className="grid grid-cols-4 gap-4">
      <TextInput
        label={t("Name")}
        field="name"
        text={ahu.designation}
        onTextChange={(name) => onAhuChange({ ...ahu, designation: name })}
        errors={inputValidationErrorMessages(error, "name")}
      />
      <TextInput
        label={t("Standort")}
        field="location"
        text={ahu.location}
        onTextChange={(location) => onAhuChange({ ...ahu, location })}
        errors={inputValidationErrorMessages(error, "location")}
      />
      <TextInput
        label={t("Baujahr")}
        field="constructionYear"
        text={ahu.constructionYear}
        onTextChange={(constructionYear) =>
          onAhuChange({ ...ahu, constructionYear })
        }
        errors={inputValidationErrorMessages(error, "constructionYear")}
      />
      <TextInput
        label={t("Luftstrom m³/h")}
        field="volumeFlowRate"
        text={ahu.volumeFlowRate}
        onTextChange={(volumeFlowRate) =>
          onAhuChange({ ...ahu, volumeFlowRate })
        }
        errors={inputValidationErrorMessages(error, "volumeFlowRate")}
      />
    </div>
  );
}

function InspectionModeSwitch() {
  const { active, setActive } = useInspectionContext();

  return (
    <div className="flex items-center space-x-4">
      <Label>{t("Aufnahme")}</Label>
      <Switch checked={active} onCheckedChange={setActive} />
      <Label>{t("Inspektion")}</Label>
    </div>
  );
}

function AssessmentCard({ workOrder }: { workOrder: WorkOrder }) {
  const { position, activeComponent } = useInspectionContext();
  const [tabValue, setTabValue] = useState("structural");

  const { notReadyError } = useErrDocumentNotReady();
  const errorPrefix = `position.${position.id}.`;

  const hasStructuralErrs = hasFieldError(
    notReadyError,
    `${errorPrefix}structuralAssessment`,
  );

  const hasVisualErrs = hasFieldError(
    notReadyError,
    `${errorPrefix}visualAssessment`,
  );

  const hasMicrobialErrs = hasFieldError(
    notReadyError,
    `${errorPrefix}microbialAssessment`,
  );

  const hasPhotoErrs = hasFieldError(notReadyError, `${errorPrefix}photo`);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {activeComponent && activeComponent.name}
          {!activeComponent && (
            <span className="text-muted-foreground font-normal italic">
              {t("- bitte Komponente auswählen -")}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={tabValue} onValueChange={setTabValue}>
          <div className="flex justify-between">
            <TabsList>
              <TabsTrigger
                value="structural"
                className={cn(
                  hasStructuralErrs && "border border-red-500 shadow-md",
                )}
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                <span>{t("Konstruktiv")}</span>
              </TabsTrigger>
              <TabsTrigger
                value="visual"
                className={cn(
                  hasVisualErrs && "border border-red-500 shadow-md",
                )}
              >
                <Eye className="mr-2 h-4 w-4" />
                <span>{t("Visuell")}</span>
              </TabsTrigger>
              <TabsTrigger
                value="microbial"
                className={cn(
                  hasMicrobialErrs && "border border-red-500 shadow-md",
                )}
              >
                <Tags className="mr-2 h-4 w-4" />
                <span>{t("Proben")}</span>
              </TabsTrigger>
              <TabsTrigger
                value="photo"
                className={cn(
                  hasPhotoErrs && "border border-red-500 shadow-md",
                )}
              >
                <Camera className="mr-2 h-4 w-4" />
                <span>{t("Fotos")}</span>
              </TabsTrigger>
            </TabsList>
            {tabValue === "structural" && (
              <AddStructuralAssessmentButton workOrder={workOrder} />
            )}
            {tabValue === "visual" && (
              <AddVisualAssessmentButton workOrder={workOrder} />
            )}
            {tabValue === "microbial" && (
              <AddMicrobialAssessmentButton workOrder={workOrder} />
            )}
            {tabValue === "photo" && <AddPhotoButton workOrder={workOrder} />}
          </div>
          <TabsContent value="structural" className="h-[65vh]">
            <StructuralInspection workOrder={workOrder} />
          </TabsContent>
          <TabsContent value="visual" className="h-[65vh]">
            <VisualInspection workOrder={workOrder} />
          </TabsContent>
          <TabsContent value="microbial" className="h-[65vh]">
            <MicrobialInspection workOrder={workOrder} />
          </TabsContent>
          <TabsContent value="photo" className="h-[65vh]">
            <PhotoDocumentation workOrder={workOrder} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
